import React, { useState } from 'react';
import { adocSectionIdDataAttribute, adocSectionTitleDataAttribute } from '../../const';
import ScrollLink from '../scroll/scrollLink';

type Props = {
  element: Element;
  subItems?: { element: Element; subItems: any[] }[];
  openSections?: Set<string>;
  onToggle?: (id: string) => void;
  onSetActive?: (id: string, parentId?: string) => void;
  parentId?: string;
};

const CollapseIcon = ({ open }: { open: boolean }) => (
  <svg
    className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
  </svg>
);

const TocMenuItem = ({ element, subItems = [], openSections, onToggle, onSetActive, parentId }: Props) => {
  const [isSelected, setIsSelected] = useState(false);
  const elementId = element.getAttribute(adocSectionIdDataAttribute);

  if (!elementId) return null;

  const isOpen = openSections?.has(elementId) ?? false;
  const itemRef = React.useRef<HTMLDivElement>(null);

  const hasChildren = subItems.length > 0;

  let className = '';
  switch (element.nodeName) {
    case 'H2':
      className = `${isSelected ? 'toc-element-h2-selected' : 'toc-element-h2'}`;
      break;
    case 'H3':
      className = `${isSelected ? 'toc-element-h3-selected' : 'toc-element-h3'}`;
      break;
    case 'H4':
      className = `${isSelected ? 'toc-element-h4-selected' : 'toc-element-h4 '}`;
      break;
  }

  const rawTitle = element.getAttribute(adocSectionTitleDataAttribute) || '';
  const title = rawTitle
    .replace(/:ref:`([^`]+)`/g, '$1')
    .replace(/flyteidl\./g, '')
    .replace(/ref_/g, '');

  return (
    <div ref={itemRef}>
      <div className="flex items-stretch">
        <div className="w-6 mx-1 flex items-center justify-center shrink-0 ">
          {hasChildren && (
            <button onClick={() => onToggle?.(elementId)} className="p-1 cursor-pointer dark:text-bright-white">
              <CollapseIcon open={isOpen} />
            </button>
          )}
        </div>
        <ScrollLink
          toId={elementId}
          smooth={false}
          hashSpy={true}
          onSetActive={() => {
            setIsSelected(true);
            onSetActive?.(elementId, parentId);
            itemRef.current?.scrollIntoView({ block: 'nearest', behavior: 'instant' });
          }}
          onSetInactive={() => setIsSelected(false)}
        >
          <div className={className}>{title}</div>
        </ScrollLink>
      </div>
      {hasChildren && (
        <div className={`ml-2 ${!isOpen ? 'hidden' : ''}`}>
          {subItems.map((subItem) => (
            <TocMenuItem
              key={subItem.element.getAttribute(adocSectionIdDataAttribute)}
              element={subItem.element}
              subItems={subItem.subItems}
              openSections={openSections}
              onToggle={onToggle}
              onSetActive={onSetActive}
              parentId={elementId}
            />
          ))}
        </div>
      )}
    </div>
  );
};
export default TocMenuItem;
